'use client'

import type { ChainId, MarketId } from '@morpho-org/blue-sdk'
import type { Address } from 'viem'

import { AdlAuthDetector, useAdlSunsetNudge } from '@/components/common/AdlSunsetNudge'
import { useIsAutoDeleverageEnabled } from '@/components/common/AutoDeleverage/hooks/useIsAutoDeleverageEnabled'
import { AdlSunsetNudgeDialog } from '@/components/common/Dialog'
import { useWeb3Context } from '@/contexts/Web3Context'

interface MarketAdlSunsetNudgeWatcherProps {
  marketId: MarketId
  chainId: ChainId
}

// Per-market dismissal: a user is nudged once per market they have
// Auto-Deleverage authorized on, independently of the dashboard nudge.
const DISMISSED_KEY_PREFIX = 'morpho.adl-sunset-nudge-dismissed.market.'

/**
 * Renders nothing; on the single market page, opens the Auto-Deleverage sunset
 * nudge once if the connected wallet has the feature authorized on this market.
 * The market page has no viewed-account concept, so the connected wallet is
 * always "self". Mount outside the tabs so it runs regardless of active tab.
 */
const MarketAdlSunsetNudgeWatcher = ({ marketId, chainId }: MarketAdlSunsetNudgeWatcherProps) => {
  const { account } = useWeb3Context()
  const isAutoDeleverageEnabled = useIsAutoDeleverageEnabled(chainId)

  if (!account.isConnected || !account.address) return null
  if (!isAutoDeleverageEnabled) return null

  return (
    <MarketAdlSunsetNudgeWatcherInner
      // Remount when the connected wallet OR the market changes so the
      // open-once ref resets. Without the market in the key, navigating
      // client-side between market pages reuses the same instance (the route
      // segment is shared) and the nudge would only ever fire for the first
      // market visited. The per-market dismissed flag is keyed independently.
      key={`${account.address}-${chainId}-${marketId}`}
      marketId={marketId}
      chainId={chainId}
      address={account.address}
    />
  )
}

interface MarketAdlSunsetNudgeWatcherInnerProps extends MarketAdlSunsetNudgeWatcherProps {
  address: Address
}

const MarketAdlSunsetNudgeWatcherInner = ({
  marketId,
  chainId,
  address
}: MarketAdlSunsetNudgeWatcherInnerProps) => {
  const { dismissed, requestOpen } = useAdlSunsetNudge({
    dismissalKey: `${DISMISSED_KEY_PREFIX}${address.toLowerCase()}.${chainId}.${marketId.toLowerCase()}`,
    source: 'market_page',
    chainId,
    marketId,
    renderDialog: () => (
      <AdlSunsetNudgeDialog source="market_page" chainId={chainId} marketId={marketId} />
    )
  })

  if (dismissed) return null

  return <AdlAuthDetector marketId={marketId} chainId={chainId} onAuthorized={requestOpen} />
}

export default MarketAdlSunsetNudgeWatcher
